<template>
  <div
    id="epicWidgetContainer"
    ref="rootEl"
    class="u-posRelative"
    tabindex="-1"
    aria-labelledby="epicWidgetContainerLabel"
  >
    <h3 id="epicWidgetContainerLabel" class="u-hiddenVisually">{{ name }} Scheduling App</h3>
    <div v-if="isDev" id="epicScheduler" class="u-pad4 u-textCenter u-bgSilver reason-first">
      <p><strong>[Epic scheduling widget placeholder]</strong></p>
      <p class="u-textShrink1">dept: {{ id }} | visitType: {{ visitType }}</p>
    </div>
    <template v-else>
      <div id="epicScheduler" class="reason-first"></div>
      <div ref="overlayRef" class="Overlay u-posAbsolute is-shown">
        <div class="Circle" style="width: 5em">
          <div class="Circle-content">
            <div class="Spinner u-textGrow6" role="status">
              <span class="u-hiddenVisually">Loading…</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
//import {useAppStore} from "@/stores/appStore.js";

//const appStore = useAppStore();

const props = defineProps({
  id: String,
  visitType: String,
  provider: String,
  name: String,
})

const overlayRef = ref(null)
const rootEl = ref(null)
const isDev = import.meta.env.DEV
const IFRAME_TITLE = props.name + ' Epic Appointment Scheduling Portal'
// Per Epic's widget docs, widget_sdk.js must be loaded from the same MyChart
// host/instance as the "url" passed to renderWidget below — a prod-loaded SDK
// talking to a non-prod instance (or vice versa) fails inside the SDK.
const WIDGET_SDK_SRC = `${window.appConfig.apiBaseUrl}/Scripts/lib/Widget/widget_sdk.js`

// Module-level (not per-instance) so concurrent/rapid remounts share one
// in-flight load instead of each appending their own widget_sdk.js tag.
let widgetSdkPromise = null

function loadWidgetSdk() {
  if (window.mychartWidget) return Promise.resolve()
  if (!widgetSdkPromise) {
    widgetSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = WIDGET_SDK_SRC
      script.async = true
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }
  return widgetSdkPromise
}

let iframeObserver = null
let iframeFallbackTimer = null

function hideOverlay() {
  if (overlayRef.value) {
    overlayRef.value.classList.remove('is-shown')
    overlayRef.value.classList.add('u-hidden')
  }
  clearTimeout(iframeFallbackTimer)
}

function stopWatchingForIframe() {
  iframeObserver?.disconnect()
  iframeObserver = null
}

// Epic's SDK injects the <iframe> itself with no completion callback, so we can't know
// when it's actually ready. A MutationObserver on the container catches the element as soon
// as it exists — but the browser inserts an <iframe> immediately, well before its content
// (Epic's calendar) has loaded, so we still wait for the iframe's own "load" event before
// hiding the spinner. The fallback timer covers both a stuck SDK that never inserts an
// iframe and an iframe whose "load" event never fires.
function watchForIframe() {
  const container = document.getElementById('epicScheduler')
  if (!container) return

  const attachToIframe = (iframe) => {
    iframe.title = IFRAME_TITLE
    stopWatchingForIframe()
    iframe.addEventListener('load', hideOverlay, { once: true })
  }

  const existingIframe = container.querySelector('iframe')
  if (existingIframe) {
    attachToIframe(existingIframe)
  } else {
    iframeObserver = new MutationObserver(() => {
      const iframe = container.querySelector('iframe')
      if (iframe) attachToIframe(iframe)
    })
    iframeObserver.observe(container, { childList: true, subtree: true })
  }

  iframeFallbackTimer = setTimeout(() => {
    stopWatchingForIframe()
    hideOverlay()
  }, 8000)
}

function renderWidget() {
  if (window.mychartWidget) {
    window.mychartWidget.renderWidget({
      url: window.appConfig.apiBaseUrl,
      apiKey: window.appConfig.apiKey,
      widgetType: 'openscheduling',
      containerId: 'epicScheduler',
      additionalParams: {
        dept: props.id,
        vt: props.visitType,
        id: props.provider,
      },
    })

    watchForIframe()
  } else {
    // Handle case when mychartWidget doesn't load
    console.error('mychartWidget not available')

    // Create and append error message
    const errorParagraph = document.createElement('p')
    errorParagraph.setAttribute('role', 'alert')
    errorParagraph.textContent =
      'Unable to load scheduling widget. Please try again later or contact us directly.'
    const schedulerContainer = document.getElementById('epicScheduler')
    if (schedulerContainer) {
      schedulerContainer.appendChild(errorParagraph)
    }
    hideOverlay()
  }
}

onUnmounted(() => {
  stopWatchingForIframe()
})

onMounted(() => {
  // Move focus into the widget section as soon as it mounts (App.vue only mounts this
  // component after a specialty is selected) so keyboard/screen-reader users landing here
  // aren't stuck tabbing past the specialty buttons with no cue anything changed.
  // preventScroll — App.vue's own scrollIntoView already handles the smooth scroll.
  rootEl.value?.focus({ preventScroll: true })

  if (isDev) return

  // ponytail: widget_sdk.js is a singleton SDK — reloading/re-executing it on every
  // specialty switch corrupts its internal state ("n[e] is not a function"). loadWidgetSdk()
  // shares one in-flight load across remounts so rapid specialty switching can't append a
  // second <script> tag before the first one finishes.
  loadWidgetSdk().then(renderWidget, renderWidget)
})
</script>
